import React, { useState, useEffect } from 'react';
import { TreeDomain } from './TreeNode';
import DomainMergeModal from './DomainMergeModal';
import './HierarchyTree.css';

function getDomainDeckCount(domainData) {
  return Object.values(domainData?.exams || {}).reduce((domainTotal, examData) => {
    return domainTotal + Object.values(examData?.topics || {}).reduce((topicTotal, topicData) => {
      return topicTotal + (topicData?.decks?.length || 0);
    }, 0);
  }, 0);
}

/**
 * HierarchyTree - Reusable collapsible tree component for displaying domain/exam/topic hierarchy
 *
 * Props:
 * - hierarchy: Object with domain -> exam -> topic structure
 * - showCheckboxes: Boolean to show/hide checkboxes
 * - selectedDomains: Set of selected domain keys
 * - selectedExams: Set of selected exam keys
 * - selectedTopics: Set of selected topic keys
 * - selectedTopic: String of current navigation topic (for Dashboard highlighting)
 * - onToggleDomain: Callback(domain) when domain checkbox clicked
 * - onToggleExam: Callback(domain, examId) when exam checkbox clicked
 * - onToggleTopic: Callback(domain, examId, topic) when topic checkbox clicked
 * - onTopicClick: Callback(domain, topic) when topic label clicked (for navigation)
 */
const HierarchyTree = ({
  hierarchy,
  showCheckboxes = false,
  selectedDomains = new Set(),
  selectedExams = new Set(),
  selectedTopics = new Set(),
  selectedTopic = null,
  onToggleDomain = () => {},
  onToggleExam = () => {},
  onToggleTopic = () => {},
  onTopicClick = null,
  onExamClick = null,
  onDeleteDeck = null,
  onMoveDomain = null,
  onDomainClick = null,
  onRenameDomain = null,
  onAddToExam = null,
  onMergeDomain = null,
}) => {
  // Expand/collapse state — expand all nodes whenever hierarchy changes.
  // Since the sidebar now only shows active exam + unlinked, everything
  // should be visible by default. Users can still manually collapse.
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [manuallyCollapsed, setManuallyCollapsed] = useState(new Set());
  const [mergeModalState, setMergeModalState] = useState(null);

  useEffect(() => {
    if (!hierarchy || Object.keys(hierarchy).length === 0) return;
    const nodes = new Set();
    Object.keys(hierarchy).forEach(domain => {
      nodes.add(domain);
      const domainData = hierarchy[domain];
      Object.keys(domainData.exams || {}).forEach(examKey => {
        if (examKey !== 'unlinked') nodes.add(`${domain}::${examKey}`);
      });
    });
    // Respect any nodes the user manually collapsed
    for (const key of manuallyCollapsed) nodes.delete(key);
    setExpandedNodes(nodes);
  }, [hierarchy, manuallyCollapsed]);

  // Toggle expand/collapse for a node, tracking manual collapses
  const toggleExpand = (nodeKey) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeKey)) {
        newSet.delete(nodeKey);
        setManuallyCollapsed(mc => { const n = new Set(mc); n.add(nodeKey); return n; });
      } else {
        newSet.add(nodeKey);
        setManuallyCollapsed(mc => { const n = new Set(mc); n.delete(nodeKey); return n; });
      }
      return newSet;
    });
  };

  // Empty state
  if (!hierarchy || Object.keys(hierarchy).length === 0) {
    return (
      <div className="hierarchy-tree">
        <div className="tree-empty">
          <p>No topics yet. Upload decks to get started.</p>
        </div>
      </div>
    );
  }

  // Get domain keys sorted: NAPLEX Review first, PTCB Review second, then by most recent topic upload
  const domains = [...Object.keys(hierarchy)].sort((a, b) => {
    if (a === 'NAPLEX Review') return -1;
    if (b === 'NAPLEX Review') return 1;
    if (a === 'PTCB Review') return -1;
    if (b === 'PTCB Review') return 1;
    const getNewest = (domainData) => {
      let newest = null;
      Object.values(domainData.exams || {}).forEach(exam => {
        Object.values(exam.topics || {}).forEach(t => {
          if (t.uploadedAt && (!newest || new Date(t.uploadedAt) > new Date(newest)))
            newest = t.uploadedAt;
        });
      });
      return newest;
    };
    const dA = getNewest(hierarchy[a]);
    const dB = getNewest(hierarchy[b]);
    if (dA && dB) return new Date(dB) - new Date(dA);
    if (dA) return -1;
    if (dB) return 1;
    return a.localeCompare(b);
  });

  const sortedDomainOptions = [...Object.keys(hierarchy)].sort((a, b) => a.localeCompare(b));

  const openMergeModal = (domain) => {
    const domainData = hierarchy?.[domain];
    if (!domainData) return;
    setMergeModalState({
      sourceDomain: domain,
      deckCount: getDomainDeckCount(domainData),
    });
  };

  const closeMergeModal = () => setMergeModalState(null);

  const handleMergeConfirm = async (destination) => {
    if (!mergeModalState || !onMergeDomain) return;
    await onMergeDomain(mergeModalState.sourceDomain, destination);
    closeMergeModal();
  };

  return (
    <div className="hierarchy-tree">
      <div className="sidebar-tree">
        {domains.map(domain => {
          const domainData = hierarchy[domain];

          // Get all exam keys for topic count (including unlinked)
          const allExamKeys = Object.keys(domainData.exams || {});

          const totalTopicCount = allExamKeys.reduce((sum, key) =>
            sum + (domainData.exams[key]?.topicCount || 0), 0);

          // Skip domains with no topics at all
          if (totalTopicCount === 0) return null;

          const isDomainExpanded = expandedNodes.has(domain);
          const isDomainSelected = selectedDomains.has(domain);

          return (
            <TreeDomain
              key={domain}
              domain={domain}
              domainData={domainData}
              topicCount={totalTopicCount}
              isExpanded={isDomainExpanded}
              onToggleExpand={() => toggleExpand(domain)}
              isChecked={isDomainSelected}
              onCheck={() => onToggleDomain(domain)}
              showCheckboxes={showCheckboxes}
              selectedExams={selectedExams}
              selectedTopics={selectedTopics}
              selectedTopic={selectedTopic}
              onToggleExam={onToggleExam}
              onToggleTopic={onToggleTopic}
              onTopicClick={onTopicClick}
              onExamClick={onExamClick}
              expandedNodes={expandedNodes}
              toggleExpand={toggleExpand}
              onDeleteDeck={onDeleteDeck}
              onMoveDomain={onMoveDomain}
              onDomainClick={onDomainClick}
              onRenameDomain={onRenameDomain}
              onAddToExam={onAddToExam}
              onOpenMergeDomain={onMergeDomain ? openMergeModal : null}
            />
          );
        })}
      </div>
      {mergeModalState && onMergeDomain && (
        <DomainMergeModal
          sourceDomain={mergeModalState.sourceDomain}
          deckCount={mergeModalState.deckCount}
          domains={sortedDomainOptions.filter((domain) => domain !== mergeModalState.sourceDomain)}
          onClose={closeMergeModal}
          onConfirm={handleMergeConfirm}
        />
      )}
    </div>
  );
};

export default HierarchyTree;
